import { useMemo } from 'react';

import { useOrders } from './useOrders';

const dateFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });

/**
 * Customer roll-up for the Customers page.
 *
 * The Unify backend doesn't expose a dedicated `/api/customers/{shopId}`
 * endpoint yet (NEA-5679 backend follow-up). For now we derive everything
 * the page renders from the orders list — same data, same shop, just
 * aggregated client-side.
 *
 * Reads the canonical order shape from `@nearst/orders-data`:
 *   - customer identity → `customerName` (fallback `customerEmail`)
 *   - line value       → `products[].price × (quantity − refundedQuantity)`,
 *                        net of partial refunds (refundedQuantity is absent
 *                        on orders pre-dating them — coalesced to 0)
 *   - last activity    → `createdAt` (ms epoch from the orders table)
 *
 * Returns `null` while orders are still loading so the page can render its
 * "no customers yet" empty state without flashing zeroes.
 */
export function useCustomers(shopId) {
	const { orders, error, isLoading } = useOrders(shopId);

	const customers = useMemo(() => {
		if (!Array.isArray(orders) || orders.length === 0) return null;

		const map = new Map();
		for (const o of orders) {
			const key = o.customerName || o.customerEmail || 'Unknown';
			const e = map.get(key) ?? { name: key, orders: 0, spend: 0, last: 0 };
			e.orders += 1;
			e.spend += orderTotal(o);
			const ts = orderTimestamp(o);
			if (ts && ts > e.last) e.last = ts;
			map.set(key, e);
		}

		const all = Array.from(map.values()).sort((a, b) => b.spend - a.spend);
		const repeat = all.filter((c) => c.orders > 1);
		return {
			total: all.length,
			repeat: repeat.length,
			top: all.slice(0, 8).map((c) => ({
				...c,
				last: c.last ? dateFormatter.format(c.last) : '',
				repeat: c.orders > 1
			}))
		};
	}, [orders]);

	return { customers, error, isLoading };
}

function orderTotal(order) {
	if (!Array.isArray(order?.products)) return 0;
	return order.products.reduce((sum, p) => {
		const netQuantity = (Number(p.quantity) || 1) - (Number(p.refundedQuantity) || 0);
		return sum + (Number(p.price) || 0) * netQuantity;
	}, 0);
}

function orderTimestamp(order) {
	const ts = typeof order?.createdAt === 'number' ? order.createdAt : Date.parse(order?.createdAt || '');
	return Number.isFinite(ts) ? ts : 0;
}
